import React from "react";
import { isAxiosError } from "axios";
import axiosInstance from "../../utils/axiosInstance";
import { notify } from "../../components/alerts";

const uploadEndpoint = "/api/v1/client/uploads";

type UploadResponse = {
  success: boolean;
  message: string;
  data: {
    id: number;
    file_path: string;
    file_url: string;
  };
};

export type UploadedFile = {
  id: number;
  path: string;
  file_path: string;
  file_url: string;
  base_url: string;
};

const useUploadDocument = () => {
  const [progress, progressSet] = React.useState(false);
  const uploadIdsByPath = React.useRef(new Map<string, number>());

  const uploadDocument = async (
    files: File | File[] | null,
    module = "onboarding",
    requestedType?: "image" | "document",
  ): Promise<UploadedFile[]> => {
    if (!files) return [];
    progressSet(true);

    try {
      const selectedFiles = Array.isArray(files) ? files : [files];
      const uploaded = await Promise.all(
        selectedFiles.map(async (file) => {
          const type =
            requestedType ??
            (file.type.startsWith("image/") ? "image" : "document");
          const formData = new FormData();
          formData.append("type", type);
          formData.append("module", module);
          formData.append("file", file);

          const response = await axiosInstance.post<UploadResponse>(
            `${uploadEndpoint}`,
            formData,
          );
          if (!response.data.success) {
            throw new Error(response.data.message || "Failed to upload file");
          }

          const { id, file_path, file_url } = response.data.data;
          if (!id || !file_path || !file_url) {
            throw new Error("Upload response is missing file details");
          }
          uploadIdsByPath.current.set(file_path, id);
          uploadIdsByPath.current.set(file_url, id);
          const baseUrl = file_url.endsWith(file_path)
            ? file_url.slice(0, -(file_path.length + 1))
            : file_url;

          return {
            id,
            path: file_path,
            file_path,
            file_url,
            base_url: baseUrl,
          };
        }),
      );

      return uploaded;
    } catch (error) {
      const message = isAxiosError(error)
        ? error.response?.data?.message
        : error instanceof Error
          ? error.message
          : undefined;
      notify({ type: "error", message: message || "Failed to upload file" });
      return [];
    } finally {
      progressSet(false);
    }
  };

  const deleteDocument = async (fileIdOrPath: string | number) => {
    progressSet(true);
    try {
      const fileId =
        typeof fileIdOrPath === "number"
          ? fileIdOrPath
          : (uploadIdsByPath.current.get(fileIdOrPath) ?? fileIdOrPath);
      const response = await axiosInstance.delete(
        `${uploadEndpoint}/${fileId}`,
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to delete file");
      }
      if (typeof fileIdOrPath === "string") {
        uploadIdsByPath.current.delete(fileIdOrPath);
      }
      notify({
        type: "success",
        message: response.data.message || "File deleted successfully",
      });
      return response.data;
    } catch (error) {
      const message = isAxiosError(error)
        ? error.response?.data?.message
        : error instanceof Error
          ? error.message
          : undefined;
      notify({ type: "error", message: message });
      return false;
    } finally {
      progressSet(false);
    }
  };

  return { progress, uploadDocument, deleteDocument };
};

export default useUploadDocument;
